import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Row, Col, Radio } from 'antd';

import FormsTable from '../FormsTable';
import FormResponder from '../FormResponder';
import FormSearchInput from '../FormSearchInput';

import {
  getCustomers,
  getProjectIdMap,
} from '../../state/selectors';
import { includesTerm } from '../../helpers/helpers';
import {
  getFilters,
  getParsedDrafts,
  handleFormClick,
} from '../formHelpers';
import { getFormsHomeDraftColumns } from '../FormColumns';
import {
  DRAFT_FILTER_ALL,
  DRAFT_FILTER_ASSIGNMENT,
  DRAFT_FILTER_SELF,
} from './formsHomeHelpers';
import OnTraccrRadioGroup from '../../common/inputs/OnTraccrRadioGroup';
import { formatProjectLabelFromCompanySettings } from '../../projects/projectHelpers';

export default function FormsDraftsPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    manual: manualDrafts = {},
    assigned: assignedDrafts = {},
  } = useSelector((state) => state.forms.drafts);
  const customers = useSelector(getCustomers);
  const projectIdMap = useSelector(getProjectIdMap);
  const templates = useSelector((state) => state.forms.templates);
  const { settings = {} } = useSelector((state) => state.settings.company);

  const [draftFilter, setDraftFilter] = useState(DRAFT_FILTER_ALL);
  const [loading, setLoading] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [assignedForm, setAssignedForm] = useState({});
  const [selectedIsEdit, setSelectedIsEdit] = useState(false);
  const [selectedIsResubmit, setSelectedIsResubmit] = useState(false);
  const [searchStr, setSearchStr] = useState('');

  const {
    allDrafts,
    selfDrafts,
    assignmentDrafts,
  } = useMemo(() => getParsedDrafts({
    manualDrafts,
    assignedDrafts,
    customers,
    projectIdMap,
    templates,
  }), [manualDrafts, assignedDrafts, customers, projectIdMap, templates]);

  const filteredDrafts = useMemo(() => {
    let drafts = allDrafts;
    if (draftFilter === DRAFT_FILTER_SELF) {
      drafts = selfDrafts;
    } else if (draftFilter === DRAFT_FILTER_ASSIGNMENT) {
      drafts = assignmentDrafts;
    }
    if (!searchStr) return drafts;
    return drafts.filter((draft) => (
      (draft.templateName && includesTerm(draft.templateName, searchStr))
      || (draft.number && includesTerm(String(draft.number), searchStr))
      || (draft.project && includesTerm(draft.project, searchStr))
      || (draft.customer && includesTerm(draft.customer, searchStr))
    ));
  }, [allDrafts, selfDrafts, assignmentDrafts, draftFilter, searchStr]);

  const selfCount = selfDrafts.length;
  const assignmentCount = assignmentDrafts.length;

  const assignedCustomerFilter = useMemo(
    () => getFilters({ data: filteredDrafts, idMap: customers, key: 'customers' }),
    [filteredDrafts, customers],
  );
  const assignedProjectFilter = useMemo(
    () => getFilters({
      data: filteredDrafts,
      idMap: projectIdMap,
      key: 'projects',
      formatter: ({ name, number }) => (
        { name: formatProjectLabelFromCompanySettings({ name, number, settings }) }
      ),
    }),
    [filteredDrafts, projectIdMap],
  );

  const onContinue = useCallback(async (record) => {
    await handleFormClick({
      record,
      dispatch,
      loading,
      setLoading,
      setAssignedForm,
      setShowDetail,
      setSelectedIsEdit,
      setSelectedIsResubmit,
    });
  }, [loading]);

  const onSearch = useCallback((e) => {
    const { target: { value } = {} } = e;
    setSearchStr(value);
  }, []);

  const columns = useMemo(() => getFormsHomeDraftColumns({
    projectFilters: assignedProjectFilter,
    customerFilters: assignedCustomerFilter,
    projectIdMap,
    settings,
    t,
    onContinue,
  }), [
    assignedProjectFilter,
    assignedCustomerFilter,
    projectIdMap,
    settings,
    t,
    onContinue,
  ]);

  const filterOptions = useMemo(() => [
    { label: 'All', value: DRAFT_FILTER_ALL, count: allDrafts.length },
    { label: 'Self-started', value: DRAFT_FILTER_SELF, count: selfCount },
    { label: 'From assignment', value: DRAFT_FILTER_ASSIGNMENT, count: assignmentCount },
  ], [allDrafts.length, selfCount, assignmentCount]);

  return (
    <div>
      <Row className="forms-list-header" justify="space-between" align="middle">
        <Col>
          <FormSearchInput
            searchStr={searchStr}
            onSearch={onSearch}
            placeholder="Search drafts..."
          />
        </Col>
        <Col>
          <OnTraccrRadioGroup
            value={draftFilter}
            buttonStyle="outline"
            className="forms-home-tab-toggle"
            onChange={setDraftFilter}
          >
            {filterOptions.map(({ label, value, count }) => (
              <Radio.Button key={value} value={value}>
                {label}
                {' '}
                <span className="forms-home-tab-toggle-count">{count}</span>
              </Radio.Button>
            ))}
          </OnTraccrRadioGroup>
        </Col>
      </Row>
      <FormsTable
        data={filteredDrafts}
        columns={columns}
        onClickRow={onContinue}
        loading={loading}
      />
      <FormResponder
        visible={showDetail}
        onClose={() => setShowDetail(false)}
        assignedForm={assignedForm}
        isEdit={selectedIsEdit}
        isResubmit={selectedIsResubmit}
      />
    </div>
  );
}
